import dayjs from "dayjs";

import { ConsiderationPeriodEnum, IdName, PastMeetingCountEnum, User, UserUpdate } from "@/api/generated";
import { parseFormattedNumber } from "@/utilities/format";
import {
  JOB_CATEGORY_OPTIONS,
  LOAN_RANGE_OPTIONS,
  OCCUPATION_OPTIONS,
  PERIOD_RANGE_OPTIONS,
  PREFECTURE_OPTIONS,
} from "@/utilities/options";
import { MAX_NAME_LENGTH } from "@/validations/patterns";

import { isJobIndustryRequired, ProfileSection } from "./profileCompletion";

export const profileSectionTitles: Record<ProfileSection, string> = {
  personal: "プロフィール",
  financial: "ご職業など",
  investment: "不動産投資について",
};
export type ProfileValues = Record<string, string | number | number[]>;
export type ProfileField = {
  key: string;
  label: string;
  options?: { value: string | number; label: string }[];
  unit?: string;
  optional?: boolean;
  maxLength?: number;
  numeric?: boolean;
  multiline?: boolean;
};
const options = (values: string[]) => values.map((value) => ({ value, label: value }));
export const getProfileFields = (
  section: ProfileSection,
  values: ProfileValues,
  residenceTypes: IdName[]
): ProfileField[] => {
  if (section === "personal")
    return [
      { key: "family_name", label: "姓", maxLength: MAX_NAME_LENGTH },
      { key: "first_name", label: "名", maxLength: MAX_NAME_LENGTH },
      { key: "family_name_kana", label: "姓（カナ）", maxLength: MAX_NAME_LENGTH },
      { key: "first_name_kana", label: "名（カナ）", maxLength: MAX_NAME_LENGTH },
      { key: "birthday", label: "生年月" },
      { key: "phone_number", label: "電話番号", maxLength: 11, numeric: true },
      { key: "address_prefecture", label: "お住まいの地域", options: PREFECTURE_OPTIONS },
    ];
  if (section === "investment")
    return [
      {
        key: "consideration_period",
        label: "検討の時期",
        options: options(Object.values(ConsiderationPeriodEnum).filter((value) => value !== "それ以上")),
      },
      {
        key: "past_meeting_count",
        label: "これまでの面談の回数",
        options: options(Object.values(PastMeetingCountEnum)),
      },
      { key: "considering_property", label: "気になっている物件", optional: true, maxLength: 2000, multiline: true },
      {
        key: "approved_financial_institutions",
        label: "審査に通った金融機関",
        optional: true,
        maxLength: 100,
        multiline: true,
      },
      { key: "other_request_text", label: "その他のご要望", optional: true, maxLength: 2000, multiline: true },
    ];
  return [
    { key: "job_category", label: "ご職業", options: OCCUPATION_OPTIONS },
    ...(isJobIndustryRequired(values.job_category)
      ? [{ key: "job_industry", label: "業種", options: JOB_CATEGORY_OPTIONS }]
      : []),
    { key: "job_continuous_period", label: "勤続年数", options: PERIOD_RANGE_OPTIONS },
    { key: "annual_income", label: "年収", unit: "万円", numeric: true },
    { key: "financial_asset", label: "金融資産（預金・株式等）", options: LOAN_RANGE_OPTIONS },
    { key: "holding_property_count", label: "投資用不動産の保有数", unit: "件", numeric: true },
    ...(Number(values.holding_property_count) > 0
      ? [{ key: "real_estate_loan_amount", label: "投資用不動産のローン合計", unit: "万円", numeric: true }]
      : []),
    {
      key: "current_residence_type_id",
      label: "お住まいの家",
      options: residenceTypes.map((value) => ({ value: value.id, label: value.name })),
    },
    ...(Number(values.current_residence_type_id) === 1
      ? [{ key: "home_loan_amount", label: "住宅ローン残債", unit: "万円", numeric: true }]
      : []),
    ...(Number(values.current_residence_type_id) === 2
      ? [{ key: "monthly_rent_payment_amount", label: "家賃", unit: "円", numeric: true }]
      : []),
  ];
};

export const getProfileValues = (user: User): ProfileValues => {
  const birthday = user.birthday ? dayjs(user.birthday) : null;
  const values: ProfileValues = {
    birth_year: birthday?.isValid() ? birthday.year() : 0,
    birth_month: birthday?.isValid() ? birthday.month() + 1 : 0,
    interested_property_type_ids: user.interested_property_type_ids ?? [],
    current_residence_type_id: user.current_residence_type_id ?? 0,
  };
  for (const key of [
    "family_name",
    "first_name",
    "family_name_kana",
    "first_name_kana",
    "phone_number",
    "address_prefecture",
    "job_category",
    "job_industry",
    "job_continuous_period",
    "annual_income",
    "financial_asset",
    "holding_property_count",
    "real_estate_loan_amount",
    "home_loan_amount",
    "monthly_rent_payment_amount",
    "consideration_period",
    "past_meeting_count",
    "considering_property",
    "approved_financial_institutions",
    "other_request_text",
  ])
    values[key] = user[key] == null ? "" : String(user[key]);
  return values;
};

/** 分類の表示中の項目だけ送る。非表示になったローン等を空値で上書きしない。 */
export const buildProfileUpdate = (section: ProfileSection, values: ProfileValues): UserUpdate => {
  const request: Record<string, unknown> = {};
  for (const field of getProfileFields(section, values, [])) {
    if (field.key === "birthday") request.birthday = `${values.birth_year}-${values.birth_month}-01`;
    else if (field.numeric && field.key !== "phone_number")
      request[field.key] = parseFormattedNumber(String(values[field.key]));
    else if (field.key === "consideration_period" || field.key === "past_meeting_count")
      request[field.key] = values[field.key] || null;
    else
      request[field.key] =
        typeof values[field.key] === "string" ? (values[field.key] as string).trim() : values[field.key];
  }
  if (section === "investment") request.interested_property_type_ids = values.interested_property_type_ids;
  return request as UserUpdate;
};
